// CycleTrackerDashboard.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Grid, Paper, Tabs, Tab, Typography, CircularProgress,
  Snackbar, Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { LocalizationProvider, DateCalendar, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { CalendarRange, Settings2 } from "lucide-react";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import { API_URL } from "../../../constants";
import PageProtectedLayout from "../../../components/Page/PageProtectedLayout.jsx";
import ButtonsMain from "../../../components/Button/ButtonMain";
import ButtonForm from "../../../components/Button/ButtonForm";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";

import CycleAdministration from "./CyclePeriod/CycleAdministration";
import CycleTracker from "./CyclePeriod/CycleTracker";

import useCycleData from "./CyclePeriod/useCycleData";
import { CycleDataContext } from "./CycleDataContext";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// ─────────────────────────────────────────────────────────────────────────────
// Tab panel
// ─────────────────────────────────────────────────────────────────────────────

function TabPanel({ value, index, children }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Gender gate
// ─────────────────────────────────────────────────────────────────────────────

function MaleGenderGate() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start", minHeight: "60vh", px: 2 }}>
      <Box sx={{
        maxWidth: 560, width: "100%", textAlign: "center",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
      }}>
        <Box sx={{
          width: 68, height: 68, borderRadius: "50%",
          bgcolor: isDark
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.primary.main, 0.08),
          border: "0.5px solid",
          borderColor: isDark
            ? alpha(theme.palette.primary.main, 0.25)
            : alpha(theme.palette.primary.main, 0.2),
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
            stroke={theme.palette.primary.main} strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="9" r="5" />
            <line x1="12" y1="14" x2="12" y2="21" />
            <line x1="9" y1="18" x2="15" y2="18" />
          </svg>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h5" fontWeight={500} sx={{ lineHeight: 1.3 }}>
            {t("cyc_male_title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
            {t("cyc_male_desc")}
          </Typography>
        </Box>

        <Box sx={{
          width: "100%", p: "14px 18px", borderRadius: 5,
          border: "0.5px solid", borderColor: "divider",
          bgcolor: isDark
            ? alpha(theme.palette.background.paper, 0.4)
            : alpha(theme.palette.grey[100], 0.8),
          textAlign: "left",
        }}>
          <Typography variant="subtitle2" color="text.secondary" textAlign="center" sx={{ lineHeight: 1.65 }}>
            {t("cyc_male_note")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Female profile gate — shown when profile is postmenopause or missing
// ─────────────────────────────────────────────────────────────────────────────

function FemaleProfileGate({ profile }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const navigate = useNavigate();

  const config = {
    postmenopause: {
      title: t("cyc_gate_postmenopause_title"),
      desc:  t("cyc_gate_postmenopause_desc"),
      icon:  "◑",
    },
    null: {
      title: t("cyc_gate_noprofile_title"),
      desc:  t("cyc_gate_noprofile_desc"),
      icon:  "◌",
    },
  };

  const { title, desc, icon } = config[profile] ?? config["null"];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, pt: 4 }}>
      <Box sx={{
        maxWidth: 520, width: "100%",
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 3, textAlign: "center",
      }}>
        {/* Icon circle */}
        <Box sx={{
          width: 64, height: 64, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: isDark ? alpha(theme.palette.warning.main, 0.12) : alpha(theme.palette.warning.main, 0.08),
          border: "0.5px solid",
          borderColor: isDark ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.warning.main, 0.25),
          fontSize: "1.75rem",
          userSelect: "none",
        }}>
          {icon}
        </Box>

        {/* Text */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {desc}
          </Typography>
        </Box>

        {/* Hint box */}
        <Box sx={{
          width: "100%", p: "14px 18px", borderRadius: 4,
          border: "0.5px solid", borderColor: "divider",
          bgcolor: isDark ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.grey[100], 0.8),
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            {t("cyc_gate_change_hint")}
          </Typography>
        </Box>

        {/* CTA */}
        <ButtonsMain
          name={t("cyc_gate_profile_btn")}
          action={() => navigate("/UserAdminWelcome")}
          sx={{ px: "28px" }}
        />
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding dialog — import-past-periods flow
// ─────────────────────────────────────────────────────────────────────────────

function OnboardingDialog({ open, onClose, onSuccess }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([
    { start: null, end: null },
    { start: null, end: null },
    { start: null, end: null },
  ]);
  const [currentCycleIndex, setCurrentCycleIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const getReferenceMonth = (index) => dayjs().subtract(cycles.length - index, "month");

  const handleDayClick = (date) => {
    setCycles((prev) => {
      const updated = [...prev];
      const cycle = { ...updated[currentCycleIndex] };
      if (!cycle.start) {
        cycle.start = date;
      } else if (!cycle.end) {
        if (dayjs(date).isBefore(dayjs(cycle.start))) {
          cycle.end = cycle.start;
          cycle.start = date;
        } else {
          cycle.end = date;
        }
      } else {
        cycle.start = date;
        cycle.end = null;
      }
      updated[currentCycleIndex] = cycle;
      return updated;
    });
  };

  const handleNext = () => setCurrentCycleIndex((i) => Math.min(cycles.length - 1, i + 1));

  const handleSave = async () => {
    const valid = cycles.every((c) => c.start && c.end);
    if (!valid) {
      setSnackbar({ open: true, message: t("cyc_warn_select_all"), severity: "warning" });
      return;
    }
    const payload = {
      past_periods: cycles.map(({ start, end }) => ({
        start: dayjs(start).format("YYYY-MM-DD"),
        end:   dayjs(end).format("YYYY-MM-DD"),
      })),
    };
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/cycles/import-past-periods/`, payload);
      onSuccess();
      onClose();
    } catch (err) {
      if (err.response?.status >= 500) {
        // Cycles committed before server error — treat as success
        onSuccess();
        onClose();
        return;
      }
      const msg =
        err.response?.data?.past_periods?.[0] ||
        err.response?.data?.detail ||
        t("cyc_err_save");
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const CustomDay = (props) => {
    const { day, ...other } = props;
    const d = dayjs(day);
    let isInRange = false, isStart = false, isEnd = false;

    cycles.forEach((cycle) => {
      if (cycle.start && cycle.end) {
        if (
          d.isSameOrAfter(dayjs(cycle.start), "day") &&
          d.isSameOrBefore(dayjs(cycle.end), "day")
        ) isInRange = true;
      }
      if (cycle.start && d.isSame(dayjs(cycle.start), "day")) isStart = true;
      if (cycle.end   && d.isSame(dayjs(cycle.end),   "day")) isEnd   = true;
    });

    return (
      <PickersDay {...other} day={day} selected={isStart || isEnd} sx={{
        ...(isInRange && {
          backgroundColor: alpha(theme.palette.primary.main, 0.15),
          borderRadius: 0,
        }),
        ...(isStart && {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderTopLeftRadius: "50%",
          borderBottomLeftRadius: "50%",
        }),
        ...(isEnd && {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
        }),
        ...(isStart && isEnd && { borderRadius: "50%" }),
      }} />
    );
  };

  const isLastStep = currentCycleIndex === cycles.length - 1;
  const currentCycle = cycles[currentCycleIndex];

  return (
    <>
      <CustomDialogSmall
        open={open}
        onClose={onClose}
        title={t("cyc_setup_title")}
        content={
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid size={12}>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  {t("cyc_setup_desc")}
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle2" textAlign="center" fontWeight={700}>
                  {t("cyc_period")} {currentCycleIndex + 1} {t("cyc_of")} {cycles.length}
                </Typography>
              </Grid>

              <Grid size={12}>
                <Paper sx={{ mx: { xs: 0, sm: 4 }, borderRadius: 4 }}>
                  <DateCalendar
                    key={currentCycleIndex}
                    value={currentCycle.start || null}
                    onChange={handleDayClick}
                    maxDate={dayjs()}
                    dayOfWeekFormatter={(date) =>
                      ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][date.day()]
                    }
                    slots={{ day: CustomDay }}
                    referenceDate={getReferenceMonth(currentCycleIndex)}
                    sx={{
                      "& .MuiDayCalendar-weekDayLabel": { fontWeight: 700, fontSize: "0.75rem" },
                    }}
                  />
                </Paper>
              </Grid>

              <Grid size={12} sx={{ textAlign: "center" }}>
                {currentCycle.start && (
                  <Typography variant="caption">
                    {t("cyc_start")} <strong>{dayjs(currentCycle.start).format("DD MMM YYYY")}</strong>
                  </Typography>
                )}
                {currentCycle.end && (
                  <Typography variant="caption" sx={{ ml: 2 }}>
                    {t("cyc_end")} <strong>{dayjs(currentCycle.end).format("DD MMM YYYY")}</strong>
                  </Typography>
                )}
              </Grid>

              <Grid size={12} sx={{ textAlign: "center" }}>
                <ButtonsMain
                  name={isLastStep ? t("cyc_btn_save") : t("cyc_btn_next")}
                  onClick={isLastStep ? handleSave : handleNext}
                  disabled={isLastStep ? saving || !currentCycle.end : !currentCycle.end}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard — single hook instance shared via context
// ─────────────────────────────────────────────────────────────────────────────

export default function CycleTrackerDashboard() {
  const { state } = useAuth();
  const { t } = useTranslation();
  const gender = state?.user?.gender;

  // Female profile check
  const [femaleProfile, setFemaleProfile]       = useState(undefined); // undefined = loading
  const [profileLoading, setProfileLoading]     = useState(true);

  useEffect(() => {
    if (gender === "M") { setProfileLoading(false); return; }
    axios.get(`${API_URL}/api/female/profile`)
      .then((res) => setFemaleProfile(res.data?.profile ?? null))
      .catch((err) => {
        // 404 = no profile set yet — treat as null (not an error)
        setFemaleProfile(err.response?.status === 404 ? null : null);
      })
      .finally(() => setProfileLoading(false));
  }, [gender]);

  const cycleData = useCycleData();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [cycleCount, setCycleCount] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => { checkCycleCount(); }, []);

  async function checkCycleCount() {
    try {
      const res = await axios.get(`${API_URL}/api/cycles/`);
      const completed = (res.data || []).filter((c) => c.current_cycle_length !== null);
      setCycleCount(completed.length);
    } catch {
      setCycleCount(0);
    }
  }

  useEffect(() => {
    if (cycleCount !== null && cycleCount < 2) setOnboardingOpen(true);
  }, [cycleCount]);

  const handleOnboardingSuccess = () => {
    cycleData.loadAll();
    checkCycleCount();
  };

  return (
    <PageProtectedLayout
      headline={t("page_hl_cycle")}
      specify={t("page_sp_cycle_tracker")}
    >
      {gender === "M" ? (
        <MaleGenderGate />
      ) : profileLoading ? (
        <Box sx={{ p: 8, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (femaleProfile === "postmenopause" || femaleProfile === null) ? (
        <FemaleProfileGate profile={femaleProfile} />
      ) : (
        <CycleDataContext.Provider value={cycleData}>

          <OnboardingDialog
            open={onboardingOpen}
            onClose={() => setOnboardingOpen(false)}
            onSuccess={handleOnboardingSuccess}
          />

          <Box sx={{
            width: "100%",
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, sm: 4 },
            pt: { xs: 2, sm: 3 },
            pb: 6,
          }}>

            {/* Tab navigation — floating glass capsule, selected tab reads as a
                solid-ish "raised" pill so its label never sits directly on the
                blurred bar underneath it. */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                TabIndicatorProps={{ style: { display: "none" } }}
                sx={{
                  minHeight: 0,
                  p: 0.5,
                  borderRadius: 6,
                  bgcolor: (t) => (t.palette.mode === "dark" ? alpha("#000", 0.2) : alpha("#fff", 0.4)),
                  backdropFilter: "blur(20px) saturate(180%)",
                  WebkitBackdropFilter: "blur(20px) saturate(180%)",
                  border: (t) => `1px solid ${t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.5)"}`,
                  "& .MuiTabs-flexContainer": { gap: 0.5 },
                  "& .MuiTab-root": {
                    minHeight: 0,
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 5,
                    px: 2.25,
                    py: 1,
                    color: (t) => (t.palette.mode === "dark" ? t.palette.grey.C600 : t.palette.grey.C700),
                    transition: "background-color 0.3s ease, color 0.3s ease",
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: (t) => (t.palette.mode === "dark" ? "common.white" : t.palette.greydark.C900),
                    bgcolor: (t) => (t.palette.mode === "dark" ? alpha("#fff", 0.1) : "rgba(255,255,255,0.95)"),
                    boxShadow: (t) => (t.palette.mode === "dark"
                      ? "inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 10px rgba(0,0,0,0.35)"
                      : "inset 0 1px 0 rgba(255,255,255,0.9), 0 2px 10px rgba(0,0,0,0.08)"),
                  },
                }}
              >
                <Tab
                  icon={<CalendarRange size={18} />}
                  iconPosition="start"
                  label={t("cyc_tab_tracker")}
                />
                <Tab
                  icon={<Settings2 size={18} />}
                  iconPosition="start"
                  label={t("cyc_admin_title")}
                />
              </Tabs>
            </Box>

            {/* Calendar Tracker tab */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ maxWidth: { md: 900 }, mx: "auto" }}>
                <CycleTracker />
              </Box>
            </TabPanel>

            {/* Cycle Administration tab */}
            <TabPanel value={activeTab} index={1}>
              <Box sx={{ maxWidth: { md: 800 }, mx: "auto" }}>
                <CycleAdministration />
              </Box>
            </TabPanel>

          </Box>

        </CycleDataContext.Provider>
      )}
    </PageProtectedLayout>
  );
}
